import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiArrowsClockwiseLight } from 'react-icons/pi';
import Button from '../TailwindComponents/Button';
import Dropdown from '../TailwindComponents/Dropdown';
import Skeleton from '../TailwindComponents/Skeleton';
import Alert from '../TailwindComponents/Alert';
import { sanitizeString } from '../../../utils/validation/sanitizers';
import FeedEventItem from './FeedEventItem';
import FeedComposer from './FeedComposer';
import { feedEventLabel } from './feedPresentation';
import {
  FeedEvent,
  FeedComment,
  FeedUser,
  FeedFilters,
  FEED_ALL,
  FEED_FILTERS_NONE,
  DEFAULT_POST_TYPES,
  filterFeedEvents,
  hasActiveFilters,
  makeOptimisticComment,
  makeOptimisticEvent,
  mergeFeedEvents,
  unconfirmedComments,
  unconfirmedEvents,
  uniqueEventTypes,
} from './feedModel';

export interface ActivityFeedProps {
  /** The server's events. The caller owns fetching; this component owns display. */
  events: FeedEvent[];
  loading?: boolean;
  error?: string;
  onRefresh: () => Promise<void> | void;
  /** Omit to render a read-only feed (no composer). */
  onPostEvent?: (post: { event_type: string; message: string; metadata?: any }) => Promise<void>;
  /** Omit to hide replies entirely. */
  onCommentAdd?: (eventId: string, comment: string) => Promise<void>;
  currentUser?: FeedUser;
  /** Mention targets and the Person filter's options. */
  members?: FeedUser[];
  /** 'pane' drops the filter bar and avatars and collapses the composer. */
  density?: 'canvas' | 'pane';
  /** Post types the composer offers. */
  postTypes?: string[];
  /** Shown when there is nothing to display. Defaults to a muted line. */
  emptyState?: React.ReactNode;
  className?: string;
  'data-tour-id'?: string;
}

const MAX_MESSAGE_LENGTH = 5000;

const TIME_FILTER_OPTIONS = [
  { value: FEED_ALL, label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
];

const asSingle = (value: string | string[]): string => (Array.isArray(value) ? value[0] : value);

/**
 * THE feed. One shared surface behind the project, task, team and department
 * feeds - a chronology on a hairline rail, a filter row, and a composer pinned
 * to the bottom.
 *
 * The list owns its own scrolling with `flex-1 min-h-0 overflow-y-auto`; it
 * never measures the viewport. A `calc(100vh - 330px)` height is a guess about
 * how much chrome sits above the feed, and it is wrong in every pane, on every
 * short viewport, and the moment a header gains a line.
 *
 * Optimistic posts and comments live here as local state and are reconciled
 * against the server list by the pure functions in `feedModel` - the caller
 * only has to refresh.
 */
const ActivityFeed: React.FC<ActivityFeedProps> = ({
  events,
  loading = false,
  error = '',
  onRefresh,
  onPostEvent,
  onCommentAdd,
  currentUser,
  members = [],
  density = 'canvas',
  postTypes = DEFAULT_POST_TYPES,
  emptyState,
  className,
  ...rest
}) => {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState<FeedFilters>(FEED_FILTERS_NONE);
  const [optimisticEvents, setOptimisticEvents] = useState<FeedEvent[]>([]);
  const [optimisticComments, setOptimisticComments] = useState<Record<string, FeedComment[]>>({});
  const optimisticSeq = useRef(0);

  const showFilters = density === 'canvas';

  const nextOptimisticId = () => {
    optimisticSeq.current += 1;
    return `optimistic-${Date.now()}-${optimisticSeq.current}`;
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  /** Refresh with no spinner - the optimistic item is already on screen. */
  const silentRefresh = useCallback(async () => {
    try {
      await onRefresh();
    } catch {
      // Swallowed on purpose: the post is visible either way, and the caller
      // has already reported its own failure.
    }
  }, [onRefresh]);

  // Everything the feed shows: pending posts the server has not echoed back
  // yet, on top of the server's own list.
  const merged = useMemo(
    () => mergeFeedEvents(optimisticEvents, events),
    [optimisticEvents, events]
  );
  const visible = useMemo(() => filterFeedEvents(merged, filters), [merged, filters]);

  // Retire optimistic items once the server has echoed them back. This runs
  // after commit, never during render: dropping a pending post mid-render is
  // what made the old feed flicker between two copies of the same post.
  useEffect(() => {
    setOptimisticEvents((prev) => {
      const left = unconfirmedEvents(prev, events);
      return left.length === prev.length ? prev : left;
    });
    setOptimisticComments((prev) => {
      const byId = new Map(events.map((e) => [e.id || '', e.comments]));
      let changed = false;
      const next: Record<string, FeedComment[]> = {};
      Object.entries(prev).forEach(([eventId, pending]) => {
        const left = unconfirmedComments(pending, byId.get(eventId));
        if (left.length !== pending.length) changed = true;
        if (left.length > 0) next[eventId] = left;
      });
      return changed ? next : prev;
    });
  }, [events]);

  const pendingCommentsFor = (event: FeedEvent): FeedComment[] =>
    unconfirmedComments(optimisticComments[event.id || ''], event.comments);

  const handlePost = async ({ event_type, message }: { event_type: string; message: string }) => {
    if (!onPostEvent) return;
    // Trim + length cap only; React escapes on render and the backend runs its
    // own bleach.clean before storing.
    const clean = sanitizeString(message.trim(), MAX_MESSAGE_LENGTH, true).trim();
    if (!clean) return;

    const optimistic = makeOptimisticEvent({
      eventType: event_type,
      message: clean,
      user: currentUser || { id: 'current-user', first_name: t('You'), last_name: '', email: '' },
      id: nextOptimisticId(),
      timestamp: new Date().toISOString(),
    });
    setOptimisticEvents((prev) => [optimistic, ...prev]);

    try {
      await onPostEvent({ event_type, message: clean });
      silentRefresh();
    } catch (err) {
      setOptimisticEvents((prev) => prev.filter((e) => e.id !== optimistic.id));
      throw err;
    }
  };

  const handleComment = async (eventId: string, comment: string) => {
    if (!onCommentAdd) return;
    const clean = sanitizeString(comment.trim(), MAX_MESSAGE_LENGTH, true).trim();
    if (!clean) return;

    const optimistic = makeOptimisticComment({
      message: clean,
      user: currentUser || { id: 'current-user', first_name: t('You'), last_name: '', email: '' },
      id: nextOptimisticId(),
      timestamp: new Date().toISOString(),
    });
    setOptimisticComments((prev) => ({ ...prev, [eventId]: [...(prev[eventId] || []), optimistic] }));

    try {
      await onCommentAdd(eventId, clean);
      silentRefresh();
    } catch (err) {
      setOptimisticComments((prev) => ({
        ...prev,
        [eventId]: (prev[eventId] || []).filter((c) => c.id !== optimistic.id),
      }));
      throw err;
    }
  };

  const typeOptions = useMemo(
    () => [
      { value: FEED_ALL, label: t('All types') },
      ...uniqueEventTypes(merged).map((type) => ({ value: type, label: feedEventLabel(type) })),
    ],
    [merged, t]
  );

  const personOptions = useMemo(
    () => [
      { value: FEED_ALL, label: t('All people') },
      ...members.map((m) => ({
        value: String(m.id),
        label: `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email,
      })),
    ],
    [members, t]
  );

  if (error) {
    return (
      <div className={clsx('p-3', className)} {...rest}>
        <Alert type="error" message={error} />
      </div>
    );
  }

  const isEmpty = visible.length === 0;
  const showSkeleton = loading && merged.length === 0;

  return (
    <div className={clsx('flex h-full min-h-0 w-full flex-col gap-3', className)} {...rest}>
      {showFilters && (
        /* The filters stand on the page, not a card: `surface-canvas` gives
           them the white field fill, and the default density gives them the
           same h-9, rounded-md trigger every other Dropdown in the app has. */
        <div className="surface-canvas flex flex-shrink-0 flex-wrap items-center gap-2">
          <Dropdown
            options={typeOptions}
            value={filters.type}
            onChange={(value) => setFilters((f) => ({ ...f, type: asSingle(value) }))}
            placeholder={t('Type')}
            width="w-44"
          />
          <Dropdown
            options={personOptions}
            value={filters.person}
            onChange={(value) => setFilters((f) => ({ ...f, person: asSingle(value) }))}
            placeholder={t('Person')}
            width="w-44"
          />
          <Dropdown
            options={TIME_FILTER_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
            value={filters.time}
            onChange={(value) => setFilters((f) => ({ ...f, time: asSingle(value) }))}
            placeholder={t('Time')}
            width="w-40"
          />
          {hasActiveFilters(filters) && (
            <Button variant="outline" size="small" onClick={() => setFilters(FEED_FILTERS_NONE)}>
              {t('Clear filters')}
            </Button>
          )}
          <Button
            variant="outline"
            size="small"
            className="ms-auto"
            onClick={handleRefresh}
            disabled={loading}
            isLoading={refreshing}
            ariaLabel={t('Refresh')}
            icon={<PiArrowsClockwiseLight className="h-4 w-4" />}
          >
            {t('Refresh')}
          </Button>
        </div>
      )}

      <div className="custom-scrollbar-main min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        {showSkeleton ? (
          <Skeleton variant="list" rows={4} />
        ) : isEmpty ? (
          <div className="py-8 text-center text-sm text-fg-muted">
            {emptyState ?? t('No activity yet')}
          </div>
        ) : (
          <ol className="relative space-y-3 py-1">
            {/* The rail the glyph discs sit on. */}
            <span aria-hidden className="absolute start-3 top-2 bottom-2 w-px bg-hairline-soft" />
            {visible.map((event, idx) => (
              <FeedEventItem
                key={event.id || `event-${idx}`}
                event={event}
                pendingComments={pendingCommentsFor(event)}
                currentUser={currentUser}
                members={members}
                onCommentAdd={onCommentAdd ? handleComment : undefined}
                density={density}
              />
            ))}
          </ol>
        )}
      </div>

      {onPostEvent && (
        <FeedComposer
          onPost={handlePost}
          members={members}
          postTypes={postTypes}
          density={density}
        />
      )}
    </div>
  );
};

export default ActivityFeed;
